import { Contract } from '@ethersproject/contracts'
import { ChainId, WAVAX } from '@yetiswap/sdk'
import { abi as IYetiSwapPairABI } from '@yetiswap/exchange-contracts/artifacts/contracts/yetiswap-core/interfaces/IYetiSwapPair.sol/IYetiSwapPair.json'
import { abi as STAKING_REWARDS_ABI } from '@yetiswap/governance/artifacts/contracts/StakingRewards.sol/StakingRewards.json'
import { abi as MERKLE_AIRDROP_ABI } from '@yetiswap/governance/artifacts/contracts/MerkleAirdrop.sol/MerkleAirdrop.json'
import { abi as YTS_ABI } from '@yetiswap/governance/artifacts/contracts/YTS.sol/YTS.json'
import { useMemo } from 'react'
import ENS_PUBLIC_RESOLVER_ABI from '../constants/abis/ens-public-resolver.json'
import { ERC20_BYTES32_ABI } from '../constants/abis/erc20'
import ERC20_ABI from '../constants/abis/erc20.json'
import SINGLE_STAKING_REWARD_INTERFACE_ABI from '../constants/abis/SingleStakingReward.json'
import YTSFaucet from '../constants/abis/YTSFaucet.json'
import { MIGRATOR_ABI, MIGRATOR_ADDRESS } from '../constants/abis/migrator'
import WETH_ABI from '../constants/abis/weth.json'
import ERC721ABI from '../constants/abis/ERC721ABI.json'
import MARKET_PLACE_ABI from '../constants/abis/market-place.json'
import YETISWAP_NFT_COLLECTIN_ABI from '../constants/abis/yetiswap-nft-collection-abi.json'
import YETI_SUMMER_COLLECTION_ABI from '../constants/abis/yeti-summer-collection-abi.json'
import NOVAX_ABI from '../constants/abis/novax-abi.json'
import AVA_PEP_ABI from '../constants/abis/avapepe-abi.json'
import TINY_DRAGON_ABI from '../constants/abis/tinydragon-abi.json'
import HEART_NFT_ABI from '../constants/abis/nft-heart.json'
import NFT_STAKE_ABI from '../constants/abis/nft-stake-abi.json'
import { MULTICALL_ABI, MULTICALL_NETWORKS } from '../constants/multicall'
import { V1_EXCHANGE_ABI, V1_FACTORY_ABI, V1_FACTORY_ADDRESSES } from '../constants/v1'
import { getContract } from '../utils'
import { useActiveWeb3React } from './index'
import {
  MARKET_PLACE_ADDRESS,
  MERKLE_AIRDROP_ADDRESS,
  YETI_SUMMER_COLLECTION_ADDRESS,
  YETISWAP_NFT_COLLECTION_ADDRESS,
  YTS_FAUCET,
  BURN_ADDRESS,
  YTS,
  HEARTS_NFT_ADDRESS,
  BOBA
} from '../constants'

// returns null on errors
export function useContract(address: string | undefined, ABI: any, withSignerIfPossible = true): Contract | null {
  const { library, account } = useActiveWeb3React()

  return useMemo(() => {
    if (!address || !ABI || !library) return null
    try {
      return getContract(address, ABI, library, withSignerIfPossible && account ? account : undefined)
    } catch (error) {
      console.error('Failed to get contract', error)
      return null
    }
  }, [address, ABI, library, withSignerIfPossible, account])
}

export function useV1FactoryContract(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId && V1_FACTORY_ADDRESSES[chainId], V1_FACTORY_ABI, false)
}

export function useV2MigratorContract(): Contract | null {
  return useContract(MIGRATOR_ADDRESS, MIGRATOR_ABI, true)
}

export function useV1ExchangeContract(address?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(address, V1_EXCHANGE_ABI, withSignerIfPossible)
}

export function useTokenContract(tokenAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(tokenAddress, ERC20_ABI, withSignerIfPossible)
}

export function useWETHContract(withSignerIfPossible?: boolean): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? WAVAX[chainId].address : undefined, WETH_ABI, withSignerIfPossible)
}

export function useENSResolverContract(address: string | undefined, withSignerIfPossible?: boolean): Contract | null {
  return useContract(address, ENS_PUBLIC_RESOLVER_ABI, withSignerIfPossible)
}

export function useBytes32TokenContract(tokenAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(tokenAddress, ERC20_BYTES32_ABI, withSignerIfPossible)
}

export function usePairContract(pairAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(pairAddress, IYetiSwapPairABI, withSignerIfPossible)
}

export function useMulticallContract(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId && MULTICALL_NETWORKS[chainId], MULTICALL_ABI, false)
}

export function useStakingContract(stakingAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(stakingAddress, STAKING_REWARDS_ABI, withSignerIfPossible)
}

export function useSingleStakingContract(stakingAddress?: string, withSignerIfPossible?: boolean): Contract | null {
  return useContract(stakingAddress, SINGLE_STAKING_REWARD_INTERFACE_ABI, withSignerIfPossible)
}

export function useMerkleAirdropContract(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? MERKLE_AIRDROP_ADDRESS[chainId] : undefined, MERKLE_AIRDROP_ABI, true)
}

export function useBurnContract(): Contract | null {
  return useContract(BURN_ADDRESS[ChainId.BOBA], ERC20_ABI, true)
}

export function useYTSContract(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? YTS[chainId].address : YTS[ChainId.BOBA].address, ERC20_ABI, true)
}

export function useYTSContractV2(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? YTS[chainId].address : YTS[ChainId.BOBA].address, YTS_ABI, true)
}

export function useERC721Contract(nftContractAddress: string): Contract | null {
  return useContract(nftContractAddress, ERC721ABI, true)
}

export function useMarketPlaceContract(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? MARKET_PLACE_ADDRESS[chainId] : MARKET_PLACE_ADDRESS[ChainId.BOBA], MARKET_PLACE_ABI, true)
}

export function useYetiSwapNFTCollectionContract(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? YETISWAP_NFT_COLLECTION_ADDRESS[chainId] : YETISWAP_NFT_COLLECTION_ADDRESS[ChainId.BOBA], YETISWAP_NFT_COLLECTIN_ABI, true)
}

export function useYTSFaucet(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? YTS_FAUCET[chainId] : undefined, YTSFaucet, true)
}

export function useYetiSummerCollectionContract(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? YETI_SUMMER_COLLECTION_ADDRESS[chainId] : undefined, YETI_SUMMER_COLLECTION_ABI, true)
}

export function useNFTStakeContract(contractAddress: string): Contract | null {
  return useContract(contractAddress, NFT_STAKE_ABI, true)
}

/*collections*/

export function useNovaXContract(): Contract | null {
  return useContract('0x2aa2a9ef24a209f47f42Cb97Bd19D881e33F3956', NOVAX_ABI, true)
}

export function useAvaPepeContract(): Contract | null {
  return useContract('0x65787a9ABC4563316B20ce6c46d0f46F4e357c36', AVA_PEP_ABI, true)
}

export function useTinyDragonContract(): Contract | null {
  return useContract('0x2144A0eB052DD991Ec8f34b6a46D2d2Eb765C026', TINY_DRAGON_ABI, true)
}

export function useHeartNftContract(): Contract | null {
  const { chainId } = useActiveWeb3React()
  return useContract(chainId ? HEARTS_NFT_ADDRESS[chainId] : undefined, HEART_NFT_ABI, true)
}
